import { Check } from "lucide-react";
import { getCreatorPopup, type CreatorPopup } from "@content/creatorPopUps";
import { cn } from "@/lib/utils";

type ReferralToastProps = {
  toastId: string | number;
  referralCode: string;
  onClose: (toastId: string | number) => void;
  onOrderNow: (toastId: string | number) => void;
};

type CreatorCardProps = {
  creatorPopup: CreatorPopup;
};

function CreatorCard({ creatorPopup }: CreatorCardProps) {
  return (
    <div
      className={cn(
        "flex w-full flex-col items-center rounded-lg bg-[#faf9f7] p-6 pb-5 text-center text-[#54524e]",
        creatorPopup.callout ? "gap-4" : "gap-2",
      )}
    >
      <div
        className={cn(
          "flex w-full items-center justify-center",
          creatorPopup.callout ? "gap-4" : "gap-0",
        )}
      >
        <div
          className={cn(
            "shrink-0 overflow-hidden",
            creatorPopup.callout ? "size-[34px] rounded-full" : "size-[148px]",
          )}
        >
          <img
            src={creatorPopup.image.src.src}
            alt={creatorPopup.image.alt}
            className="size-full object-cover"
          />
        </div>
        {creatorPopup.callout ? (
          <p className="whitespace-nowrap font-trimMono text-[16px] uppercase leading-[1.6] tracking-[0.48px]">
            {creatorPopup.callout}
          </p>
        ) : null}
      </div>

      {creatorPopup.quote ? (
        <div className="flex w-full flex-col items-center justify-center gap-3">
          <p className="text-center text-[14px] leading-[1.6] tracking-[-0.28px]">
            {creatorPopup.quote}
          </p>
          {creatorPopup.signatureImage ? (
            <div className="shrink-0 overflow-hidden">
              <img
                src={creatorPopup.signatureImage.src.src}
                alt={creatorPopup.signatureImage.alt}
                className="h-[46px] w-auto object-cover"
              />
            </div>
          ) : (
            <p className="font-trimMono text-[14px] uppercase leading-[1.6] tracking-[0.3px]">
              - {creatorPopup.firstName}
              {creatorPopup.lastName ? <> {creatorPopup.lastName}</> : null}
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function ReferralToast({
  toastId,
  referralCode,
  onClose,
  onOrderNow,
}: ReferralToastProps) {
  const creatorPopup = getCreatorPopup(referralCode);
  const rewardLine = creatorPopup?.rewardLine ?? "You unlocked a reward!";

  return (
    <div className="flex w-full flex-col items-start gap-4 rounded-lg bg-white px-4 pb-6 pt-4 shadow-[0_0_0_1px_#dbdbdb]">
      {creatorPopup ? (
        <CreatorCard creatorPopup={creatorPopup} />
      ) : (
        <div className="flex h-[146px] w-full items-center justify-center">
          <img
            src="/referral-toast.avif"
            alt=""
            className="w-[326px] max-w-full object-contain"
          />
        </div>
      )}

      <div className="-mb-0.5 flex w-full items-center justify-center gap-4 rounded-lg bg-[rgba(74,219,144,0.15)] py-4">
        <Check
          size={16}
          strokeWidth={1.5}
          className="shrink-0 text-[#1f2022]"
          aria-hidden="true"
        />
        <p className="font-trimMono text-[14px] uppercase leading-[1.6] tracking-[0.42px] text-[#1f2022]">
          {rewardLine}
        </p>
      </div>

      <div className="flex w-full flex-col gap-2 pt-[2px] text-center">
        <p className="text-[14px] leading-[1.6] tracking-[-0.28px]">
          Buy Matic and get a{" "}
          <span className="font-semibold">free Annual Bag Pass</span>.
        </p>
        <p className="text-[12px] leading-[1.6] tracking-[-0.24px] text-[#54524e]">
          Automatically applied when you add Matic to your cart.
        </p>
      </div>

      <div className="flex w-full flex-col items-center gap-[22px]">
        <div className="w-full px-6">
          <button
            type="button"
            onClick={() => onOrderNow(toastId)}
            className="flex h-14 w-full items-center justify-center rounded-[50px] border border-black bg-[#1f2022] font-trimMono text-[16px] uppercase leading-[1.6] tracking-[0.48px] text-white transition-colors hover:bg-white hover:text-[#1f2022]"
          >
            Order now
          </button>
        </div>

        <button
          type="button"
          onClick={() => onClose(toastId)}
          className="font-trimMono text-[14px] uppercase leading-[1.6] tracking-[0.42px] text-[#1f2022] underline"
        >
          Close
        </button>
      </div>
    </div>
  );
}
